/**
 * Mobile - Handles mobile-specific UI functionality
 * 
 * Required from parent (voiceInterface):
 * - isMobile: boolean
 * - messages: Array (mobile message history)
 * - t(key): string (translation helper)
 * - shadowRoot: ShadowRoot | Document
 * 
 * State owned by this module:
 * - waveformBars: Array<number> - Waveform animation state
 * - waveformInterval: number | null - Waveform animation interval
 * - isTranscriptExpanded: boolean - Mobile transcript expansion state
 * - _mobileTimerInterval: number | null - Timer runs in bar, owns its DOM
 * - _mobileCallStartTime: number | null
 */

export class Mobile {
  constructor(voiceInterface) {
    this.voiceInterface = voiceInterface;
    this.config = voiceInterface.config;
    // State owned by this module
    this.waveformBars = [0.3, 0.5, 0.8, 0.4, 0.6, 0.9, 0.5, 0.7];
    this.waveformInterval = null;
    this.isTranscriptExpanded = false;
    this._mobileTimerInterval = null;
    this._mobileCallStartTime = null;
    // Initialize messages array on parent (owned by Mobile, accessed via voiceInterface.messages)
    if (!this.voiceInterface.messages) {
      this.voiceInterface.messages = [];
    }
  }

  /**
   * Helper to access translation method from parent
   */
  t(key) {
    return this.voiceInterface.t(key);
  }

  /**
   * Create mobile minimized bar (appends to document.body)
   */
  createMobileMinimizedBar() {
    // Remove existing bar if any
    const existingBar = document.getElementById('mobile-voice-call-bar-container');
    if (existingBar) {
      existingBar.remove();
    }
    
    // Inject / refresh mobile CSS in document head (must match current voice config each call)
    let mobileStyleEl = document.getElementById('mobile-voice-call-bar-styles');
    if (!mobileStyleEl) {
      mobileStyleEl = document.createElement('style');
      mobileStyleEl.id = 'mobile-voice-call-bar-styles';
      document.head.appendChild(mobileStyleEl);
    }
    mobileStyleEl.textContent = this.voiceInterface.styles.generateMobileCSS();
    
    // Create container div
    const container = document.createElement('div');
    container.id = 'mobile-voice-call-bar-container';
    const textDir = this.config.direction === 'rtl' ? 'rtl' : 'ltr';
    container.setAttribute('dir', textDir);
    container.innerHTML = `
      <!-- Duration Badge (hidden until first audio) -->
      <div class="mobile-duration-badge mobile-duration-badge--hidden" id="mobileDurationBadge">
        <div class="mobile-duration-dot"></div>
        <span id="mobileDurationText">00:00</span>
      </div>
      
      <!-- Main Bar -->
      <div class="mobile-voice-bar">
        <!-- Top Row: Status, Waveform/Spinner, Controls -->
        <div class="mobile-top-row">
          <!-- Status Indicator -->
          <div class="mobile-status-indicator">
            <div class="mobile-status-dot" id="mobileStatusDot"></div>
            <span class="mobile-status-text" id="mobileStatusText">${this.t('listening')}</span>
          </div>
          
          <!-- Centered: Spinner (loading) or Waveform (after first audio) -->
          <div class="mobile-waveform-center" id="mobileWaveform">
            <div class="mobile-waveform-spinner" id="mobileWaveformSpinner" aria-hidden="false">
              <div class="mobile-waveform-spinner-inner"></div>
            </div>
            <div class="mobile-waveform-bars mobile-waveform-bars--hidden" id="mobileWaveformBars">
              ${Array(7).fill(0).map((_, i) => 
                `<div class="mobile-waveform-bar" data-index="${i}"></div>`
              ).join('')}
            </div>
          </div>
          
          <!-- Controls -->
          <div class="mobile-controls">
            <button type="button" class="mobile-control-btn" id="mobileMuteBtn" aria-label="Mute microphone">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-mic-icon">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" x2="12" y1="19" y2="22"/>
              </svg>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-mic-off-icon" style="display: none;">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" x2="12" y1="19" y2="22"/>
                <line x1="4" y1="4" x2="20" y2="20" stroke="#ef4444" stroke-width="1.5" opacity="0.85"/>
              </svg>
            </button>
            
            <button type="button" class="mobile-control-btn" id="mobilePauseBtn" aria-label="Pause call">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" class="mobile-pause-icon">
                <rect x="6" y="4" width="4" height="16" rx="1"/>
                <rect x="14" y="4" width="4" height="16" rx="1"/>
              </svg>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="#fff" stroke="none" class="mobile-play-icon" style="display: none;">
                <polygon points="6,4 20,12 6,20"/>
              </svg>
            </button>
            
            <button type="button" class="mobile-control-btn" id="mobileSpeakerBtn" aria-label="Toggle speaker">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-speaker-icon">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-speaker-off-icon" style="display: none;">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                <line x1="4" y1="4" x2="20" y2="20" stroke="#ef4444" stroke-width="1.5" opacity="0.85"/>
              </svg>
            </button>
            
            <button type="button" class="mobile-end-call-btn" id="mobileEndCallBtn" aria-label="End call">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/>
                <line x1="22" x2="2" y1="2" y2="22"/>
              </svg>
            </button>
          </div>
        </div>
        
        <!-- Bottom Row: Transcript (expandable) -->
        <button type="button" class="mobile-transcript-row" id="mobileTranscriptRow">
          <p class="mobile-transcript-text" id="mobileTranscriptText" dir="${textDir}">${this.t('transcriptWillAppear')}</p>
          <div class="mobile-transcript-footer">
            <span class="mobile-expand-hint">tap to expand & type</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="opacity: 0.6;">
              <path d="M7 17L17 7M17 7H7M17 7V17"/>
            </svg>
          </div>
        </button>

        <!-- Fly-to-cart target: always visible for animation (positioned where cart icon appears) -->
        <div id="ttpFlyToCartTarget" class="ttp-fly-target" aria-hidden="true"></div>

        <!-- Cart Row - shown when items are in cart -->
        <div class="mobile-cart-row" id="mobileCartRow" style="display: none;">
          <div class="mobile-cart-row__content">
            <span class="mobile-cart-row__icon" data-ttp-fly-target>🛒</span>
            <span class="mobile-cart-row__text" id="mobileCartText">0 items · ₪0.00</span>
          </div>
        </div>
      </div>
      
      <!-- Expanded Transcript Overlay -->
      <div class="mobile-transcript-overlay" id="mobileTranscriptOverlay">
        <div class="mobile-expanded-transcript" id="mobileExpandedTranscript">
          <!-- Header -->
          <div class="mobile-transcript-header">
            <div class="mobile-header-left">
              <div class="mobile-header-status">
                <div class="mobile-header-status-dot"></div>
                <span class="mobile-header-status-text" id="mobileHeaderStatusText">${this.t('listening')}</span>
                <span class="mobile-header-duration" id="mobileHeaderDuration">00:00</span>
              </div>
              <span class="mobile-transcript-label">CONVERSATION</span>
            </div>
            <button type="button" class="mobile-close-transcript-btn" id="mobileCloseTranscriptBtn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          
          <!-- Messages Container -->
          <div class="mobile-messages-container" id="mobileMessagesContainer">
            <!-- Messages will be dynamically added here -->
          </div>
          
          <!-- Text Input Area -->
          <div class="mobile-input-area">
            <div class="mobile-input-wrapper">
              <input
                type="text"
                placeholder="Type a message..."
                id="mobileTextInput"
                class="mobile-text-input"
                dir="${textDir}"
              />
              <button type="button" class="mobile-send-btn" id="mobileSendBtn" disabled>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                </svg>
              </button>
            </div>
            <p class="mobile-input-hint">🎙️ ${this.t('voiceActive') || 'Voice is still active'} • ${this.t('typeOrSpeak') || 'Type or speak'}</p>
          </div>
        </div>
      </div>
    `;
    
    // Append to body
    document.body.appendChild(container);
    
    // Apply inline styles to buttons to ensure they're circular (highest specificity)
    const muteBtn = container.querySelector('#mobileMuteBtn');
    const pauseBtn = container.querySelector('#mobilePauseBtn');
    const speakerBtn = container.querySelector('#mobileSpeakerBtn');
    const endCallBtn = container.querySelector('#mobileEndCallBtn');
    
    if (muteBtn) {
      muteBtn.style.cssText = `
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        min-height: 32px !important;
        max-width: 32px !important;
        max-height: 32px !important;
        aspect-ratio: 1 !important;
        border-radius: 50% !important;
        overflow: hidden !important;
        padding: 0 !important;
        margin: 0 !important;
        box-sizing: border-box !important;
      `;
      // Style the visible (white) mic icon - show it, hide the red one
      const micIcon = muteBtn.querySelector('.mobile-mic-icon');
      const micOffIcon = muteBtn.querySelector('.mobile-mic-off-icon');
      
      if (micIcon) {
        micIcon.style.cssText = `
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          width: 15px !important;
          height: 15px !important;
          margin: 0 !important;
          padding: 0 !important;
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
        `;
      }
      // Hide the muted (red) mic icon
      if (micOffIcon) {
        micOffIcon.style.cssText = `
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
        `;
      }
    }
    
    if (pauseBtn) {
      pauseBtn.style.cssText = `
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        min-height: 32px !important;
        max-width: 32px !important;
        max-height: 32px !important;
        aspect-ratio: 1 !important;
        border-radius: 50% !important;
        overflow: hidden !important;
        padding: 0 !important;
        margin: 0 !important;
        box-sizing: border-box !important;
      `;
      const pauseIcon = pauseBtn.querySelector('.mobile-pause-icon');
      const playIcon = pauseBtn.querySelector('.mobile-play-icon');
      if (pauseIcon) {
        pauseIcon.style.cssText = `
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          width: 15px !important;
          height: 15px !important;
          margin: 0 !important;
          padding: 0 !important;
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
        `;
      }
      if (playIcon) {
        playIcon.style.cssText = `
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
        `;
      }
    }
    
    if (speakerBtn) {
      speakerBtn.style.cssText = `
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        min-height: 32px !important;
        max-width: 32px !important;
        max-height: 32px !important;
        aspect-ratio: 1 !important;
        border-radius: 50% !important;
        overflow: hidden !important;
        padding: 0 !important;
        margin: 0 !important;
        box-sizing: border-box !important;
      `;
      // Style the visible (white) speaker icon - show it, hide the red one
      const speakerIcon = speakerBtn.querySelector('.mobile-speaker-icon');
      const speakerOffIcon = speakerBtn.querySelector('.mobile-speaker-off-icon');
      
      if (speakerIcon) {
        speakerIcon.style.cssText = `
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          width: 15px !important;
          height: 15px !important;
          margin: 0 !important;
          padding: 0 !important;
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
        `;
      }
      // Hide the muted (red) speaker icon
      if (speakerOffIcon) {
        speakerOffIcon.style.cssText = `
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
        `;
      }
    }
    
    if (endCallBtn) {
      endCallBtn.style.cssText = `
        width: 36px !important;
        height: 36px !important;
        min-width: 36px !important;
        min-height: 36px !important;
        max-width: 36px !important;
        max-height: 36px !important;
        aspect-ratio: 1 !important;
        border-radius: 50% !important;
        overflow: hidden !important;
        padding: 0 !important;
        margin: 0 !important;
        box-sizing: border-box !important;
      `;
      const endCallSvg = endCallBtn.querySelector('svg');
      if (endCallSvg) {
        endCallSvg.style.cssText = `
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          width: 15px !important;
          height: 15px !important;
          margin: 0 !important;
          padding: 0 !important;
        `;
      }
    }
    
    // Setup event handlers for the new elements
    this.voiceInterface.setupMobileEventHandlers();
    // Timer starts when first audio plays (playbackStarted) - not when bar is created
  }

  /**
   * Hide loading spinner and show waveform when first audio frame arrives (playbackStarted).
   * Mobile only - reveals duration badge, waveform, and starts timer.
   */
  hideMobileLoadingState() {
    const container = document.getElementById('mobile-voice-call-bar-container');
    const spinner = container?.querySelector('#mobileWaveformSpinner');
    const bars = container?.querySelector('#mobileWaveformBars');
    const badge = container?.querySelector('#mobileDurationBadge');
    if (spinner) {
      spinner.classList.add('mobile-waveform-spinner--hidden');
      spinner.setAttribute('aria-hidden', 'true');
    }
    if (bars) {
      bars.classList.remove('mobile-waveform-bars--hidden');
    }
    if (badge) {
      badge.classList.remove('mobile-duration-badge--hidden');
    }
  }

  /**
   * Start mobile timer. Bar owns timer and DOM - most reliable on mobile.
   */
  startMobileTimer() {
    if (this._mobileCallStartTime != null || this._mobileTimerInterval) return;
    this._mobileCallStartTime = Date.now();
    console.log('⏱️ [Mobile] Timer started at', new Date(this._mobileCallStartTime).toISOString());
    const updateTimer = () => {
      const container = document.getElementById('mobile-voice-call-bar-container');
      if (!container || !this._mobileCallStartTime) return;
      const elapsed = Date.now() - this._mobileCallStartTime;
      const minutes = Math.floor(elapsed / 60000);
      const seconds = Math.floor((elapsed % 60000) / 1000);
      const timerText = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      const mobileDurationText = container.querySelector('#mobileDurationText');
      const mobileHeaderDuration = container.querySelector('#mobileHeaderDuration');
      if (mobileDurationText) mobileDurationText.textContent = timerText;
      if (mobileHeaderDuration) mobileHeaderDuration.textContent = timerText;
    };
    updateTimer();
    this._mobileTimerInterval = setInterval(updateTimer, 1000);
  }

  /**
   * Stop mobile timer
   */
  stopMobileTimer() {
    if (this._mobileTimerInterval) {
      clearInterval(this._mobileTimerInterval);
      this._mobileTimerInterval = null;
      console.log('⏱️ [Mobile] Timer stopped');
    }
    this._mobileCallStartTime = null;
  }

  /**
   * Remove mobile minimized bar
   */
  removeMobileMinimizedBar() {
    this.stopMobileTimer();
    const container = document.getElementById('mobile-voice-call-bar-container');
    if (container) {
      container.remove();
    }
    // Keep CSS in head for potential reuse
  }

  /**
   * When false, the "tap to expand & type" row ignores taps (connecting / disclaimer handoff).
   * Prevents ghost taps from opening the text sheet after modal buttons that sit over the bar.
   */
  setTranscriptRowInteractive(interactive) {
    const container = document.getElementById('mobile-voice-call-bar-container');
    const row = container?.querySelector('#mobileTranscriptRow');
    if (row) {
      row.style.pointerEvents = interactive ? '' : 'none';
    }
  }

  /**
   * Expand mobile transcript overlay
   */
  expandMobileTranscript() {
    if (!this.voiceInterface.isActive) {
      return;
    }
    const container = document.getElementById('mobile-voice-call-bar-container');
    const overlay = container?.querySelector('#mobileTranscriptOverlay');
    if (overlay) {
      overlay.classList.add('mobile-transcript-overlay--open');
      this.isTranscriptExpanded = true;
      
      // Focus input and sync send button state (container-scoped for Shopify)
      const input = container?.querySelector('#mobileTextInput');
      const sendBtn = container?.querySelector('#mobileSendBtn');
      if (input) {
        setTimeout(() => input.focus(), 100);
      }
      if (sendBtn && input) {
        sendBtn.disabled = !input.value.trim();
      }
      
      // Scroll to bottom of messages (container-scoped for Shopify)
      const messagesContainer = container?.querySelector('#mobileMessagesContainer');
      if (messagesContainer) {
        setTimeout(() => {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }, 100);
      }
    }
  }

  /**
   * Collapse mobile transcript overlay
   */
  collapseMobileTranscript() {
    const container = document.getElementById('mobile-voice-call-bar-container');
    const overlay = container?.querySelector('#mobileTranscriptOverlay');
    if (overlay) {
      overlay.classList.remove('mobile-transcript-overlay--open');
      this.isTranscriptExpanded = false;
    }
    const input = container?.querySelector('#mobileTextInput');
    if (input) {
      try {
        input.blur();
      } catch (_) {}
    }
  }

  /**
   * Update mobile status display
   */
  updateMobileStatus(status) {
    const container = document.getElementById('mobile-voice-call-bar-container');
    const statusText = container?.querySelector('#mobileStatusText');
    const headerStatusText = container?.querySelector('#mobileHeaderStatusText');
    const statusDot = container?.querySelector('#mobileStatusDot');
    
    if (!statusText && !headerStatusText) return;
    
    // Use custom statusText from config if provided, otherwise use translations
    const customStatusText = this.config.statusText;
    let displayText = '';
    let dotColor = '#6b7280';
    const brandPrimary =
      this.config.primaryBtnGradient1 || this.config.startCallButtonColor || '#3b82f6';
    const brandSecondary =
      this.config.primaryBtnGradient2 || this.config.avatarGradient2 || brandPrimary;
    
    if (customStatusText) {
      // Use custom status text for all states
      displayText = customStatusText;
      dotColor = this.config.statusDotColor || '#10b981';
    } else {
      // Use dynamic status based on state
      if (status === 'connecting') {
        displayText = this.t('connecting') || 'Connecting...';
        dotColor = brandPrimary;
      } else if (status === 'listening' || status === 'recording') {
        displayText = this.t('listening') || 'Listening...';
        dotColor = '#22c55e';
      } else if (status === 'speaking') {
        displayText = this.t('speaking') || 'Speaking...';
        dotColor = brandSecondary;
      } else {
        displayText = this.t('paused') || 'Paused';
        dotColor = '#facc15';
      }
    }
    
    if (statusText) statusText.textContent = displayText;
    if (headerStatusText) headerStatusText.textContent = displayText;
    if (statusDot) {
      statusDot.style.backgroundColor = dotColor;
      statusDot.style.boxShadow = status === 'paused' ? 'none' : `0 0 8px ${dotColor}`;
    }
  }

  /**
   * Add message to mobile UI
   */
  addMobileMessage(type, text) {
    // Use parent's messages array (owned by mobile but accessed via voiceInterface)
    this.voiceInterface.messages.push({ type, text });
    // Update UI (container-scoped for Shopify)
    const container = document.getElementById('mobile-voice-call-bar-container');
    const messagesContainer = container?.querySelector('#mobileMessagesContainer');
    if (messagesContainer) {
      // Render message
      const messageEl = document.createElement('div');
      messageEl.className = `mobile-message-bubble ${type}`;
      messageEl.setAttribute('dir', this.config.direction === 'rtl' ? 'rtl' : 'ltr');
      messageEl.textContent = text;
      messagesContainer.appendChild(messageEl);
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  /**
   * Start mobile waveform animation
   */
  startWaveformAnimation() {
    if (this.waveformInterval) {
      clearInterval(this.waveformInterval);
    }
    
    const container = document.getElementById('mobile-voice-call-bar-container');
    const waveform = container?.querySelector('#mobileWaveform');
    if (!waveform) return;
    
    this.waveformInterval = setInterval(() => {
      const barsContainer = waveform?.querySelector('#mobileWaveformBars');
      const bars = barsContainer ? barsContainer.querySelectorAll('.mobile-waveform-bar') : waveform?.querySelectorAll('.mobile-waveform-bar');
      if (!bars?.length) return;
      bars.forEach((bar, i) => {
        const height = Math.random() * 14 + 6;
        bar.style.height = `${height}px`;
      });
    }, 100);
  }

  /**
   * Stop mobile waveform animation
   */
  stopWaveformAnimation() {
    if (this.waveformInterval) {
      clearInterval(this.waveformInterval);
      this.waveformInterval = null;
    }

    const container = document.getElementById('mobile-voice-call-bar-container');
    const waveform = container?.querySelector('#mobileWaveform');
    if (waveform) {
      const barsContainer = waveform.querySelector('#mobileWaveformBars');
      const bars = barsContainer ? barsContainer.querySelectorAll('.mobile-waveform-bar') : waveform.querySelectorAll('.mobile-waveform-bar');
      bars.forEach(bar => {
        bar.style.height = '8px';
      });
    }
  }

  /**
   * Cleanup all mobile intervals and resources
   */
  cleanup() {
    this.stopWaveformAnimation();
    this.collapseMobileTranscript();
    this.removeMobileMinimizedBar();
    // Clear messages array
    this.voiceInterface.messages = [];
  }
}
